/**
 * Clinician diagnostic workspace (ADHD-30 to ADHD-35).
 *   /therapist/diagnostic        assessments assigned to me
 *   /therapist/diagnostic/:id    one assessment
 * The assessor records every determination; nothing is inferred from scores.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import api from '../../services/api';
import InformantPanel from '../../components/therapist/InformantPanel';
import { CONCLUSIONS, STATUS } from '../../components/therapist/DiagnosticRequestPanel';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
// ASRS shows the Part A screening count (published rule), not the whole-scale total.
const resultText = (q) => {
  const partA = (q.resultDetails?.itemCounts || []).find(c => /part_a_positive_screen$/.test(c.ruleId || ''));
  return partA ? `Part A: ${partA.count} of ${partA.of} shaded` : (q.score ?? '–');
};
const toDateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';
const DET = [['', '–'], ['present', 'Present'], ['absent', 'Absent'], ['unclear', 'Unclear']];
const SETTINGS = [['home', 'Home'], ['work_study', 'Work or study'], ['social', 'Social life'], ['driving', 'Driving'], ['finances', 'Finances'], ['other', 'Other']];

function Section({ title, children, testId }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={testId}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function MyList() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/diagnostic/me').then(r => setData(r.data)).catch(() => setData({ isAssessor: false, assessments: [] })); }, []);
  if (!data) return <CircularProgress />;
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Diagnostic assessments</Typography>
      {!data.isAssessor && <Alert severity="info" sx={{ mb: 2 }}>You do not have a verified credential for adult ADHD diagnosis. Assessments can be requested from a client's Assessments tab.</Alert>}
      {data.assessments.length === 0 ? <Typography>No assessments assigned to you.</Typography> : (
        <Table size="small">
          <TableHead><TableRow><TableCell>Client</TableCell><TableCell>Requested</TableCell><TableCell>Status</TableCell><TableCell>Conclusion</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {data.assessments.map(a => (
              <TableRow key={a._id}>
                <TableCell>{a.clientName}</TableCell>
                <TableCell>{fmtDate(a.createdAt)}</TableCell>
                <TableCell><Chip size="small" label={STATUS[a.status]} variant="outlined" /></TableCell>
                <TableCell>{a.status === 'signed' ? CONCLUSIONS[a.outcome?.conclusion] : ''}</TableCell>
                <TableCell><Button size="small" component={RouterLink} to={`/therapist/diagnostic/${a._id}`}>Open</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function Workspace({ id }) {
  const [a, setA] = useState(null);
  const [ev, setEv] = useState(null);
  const [isAssessor, setIsAssessor] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [acks, setAcks] = useState([]);
  const [audit, setAudit] = useState(null);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amend, setAmend] = useState({ reason: '', rationale: '', recommendations: '', report: '' });
  const [referral, setReferral] = useState({ recipientType: 'prescriber', recipientName: '', recipientContact: '' });

  const load = useCallback(async () => {
    try {
      const [r, e, me] = await Promise.all([api.get(`/diagnostic/${id}`), api.get(`/diagnostic/${id}/evidence`), api.get('/diagnostic/me')]);
      setA(r.data.assessment); setEv(e.data);
      setIsAssessor(me.data.isAssessor && (me.data.assessments || []).some(x => x._id === id));
    } catch (err) { setError(err.response?.data?.error || 'Could not load this assessment.'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error && !a) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!a || !ev) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const editable = isAssessor && ['requested', 'in_progress'].includes(a.status);
  const signed = a.status === 'signed';
  const setField = (path, value) => setA(prev => {
    const next = JSON.parse(JSON.stringify(prev));
    const keys = path.split('.'); let o = next;
    keys.slice(0, -1).forEach(k => { o = o[k] = o[k] || {}; });
    o[keys[keys.length - 1]] = value;
    return next;
  });
  const setCriterion = (key, field, value) => setA(prev => ({ ...prev, criteria: prev.criteria.map(c => c.key === key ? { ...c, [field]: value } : c) }));
  const setDiff = (key, field, value) => setA(prev => ({ ...prev, differentials: prev.differentials.map(d => d.key === key ? { ...d, [field]: value } : d) }));

  const call = async (fn, done) => {
    setError(null); setMsg(null);
    try { const r = await fn(); if (r?.data?.assessment) setA(r.data.assessment); if (done) setMsg(done); }
    catch (err) {
      const d = err.response?.data || {};
      setError([d.error, ...(d.blockers || []), ...((d.warnings || []).map(w => w.text))].filter(Boolean).join(' ') || 'That did not work.');
    }
  };
  const save = () => call(() => api.patch(`/diagnostic/${id}`, {
    interview: a.interview, context: a.context, outcome: a.outcome, report: { text: a.report?.text || '' },
    criteria: a.criteria.map(({ key, adulthood, childhood, examples }) => ({ key, adulthood: adulthood || null, childhood: childhood || null, examples })),
    differentials: a.differentials.map(({ key, considered, notes }) => ({ key, considered, notes }))
  }), 'Saved.');

  const selfQs = (ev.assessments || []).filter(x => x.source !== 'informant');
  const counts = a.counts || {};

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Button size="small" component={RouterLink} to="/therapist/diagnostic">Back to diagnostic assessments</Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5">Adult ADHD assessment: {ev.client?.name}</Typography>
        <Chip label={STATUS[a.status]} color={signed ? 'success' : 'default'} variant="outlined" />
        {signed && <Typography variant="body2" color="text.secondary">Signed {fmtDate(a.signedAt)}</Typography>}
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        You make every determination. Questionnaires and informant views are evidence for the interview; nothing here is worked out from scores.
      </Alert>
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Section title="Reason for referral"><Typography variant="body2">{a.requestNotes || 'Not given.'}</Typography></Section>

      <Section title="Evidence on file" testId="dx-evidence">
        <Table size="small">
          <TableHead><TableRow><TableCell>Questionnaire</TableCell><TableCell>Completed</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
          <TableBody>
            {selfQs.map((q, i) => (
              <TableRow key={i}><TableCell>{q.testName}{q.recallPeriod === 'childhood' ? ' (childhood recall)' : ''}</TableCell><TableCell>{fmtDate(q.dateTaken)}</TableCell><TableCell>{resultText(q)}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        {(ev.screens || []).length > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Latest ASRS screen: {ev.screens[0].screenScore} of {ev.screens[0].screenMax} Part A answers in the shaded range ({ev.screens[0].aboveThreshold ? 'above' : 'below'} the screening threshold).
          </Typography>
        )}
      </Section>
      <InformantPanel clientId={a.clientId} />

      <Section title="Interview">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField type="date" label="Date" InputLabelProps={{ shrink: true }} disabled={!editable} value={toDateInput(a.interview?.date)} onChange={e => setField('interview.date', e.target.value || null)} />
          <TextField type="number" label="Length (minutes)" disabled={!editable} value={a.interview?.durationMinutes ?? ''} onChange={e => setField('interview.durationMinutes', e.target.value ? Number(e.target.value) : null)} />
          <FormControl sx={{ minWidth: 160 }} disabled={!editable}>
            <InputLabel id="mod">How</InputLabel>
            <Select labelId="mod" label="How" value={a.interview?.modality || ''} onChange={e => setField('interview.modality', e.target.value || null)}>
              <MenuItem value="video">Video</MenuItem><MenuItem value="in_person">In person</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} disabled={!editable}>
            <InputLabel id="si">Semi-structured interview</InputLabel>
            <Select labelId="si" label="Semi-structured interview" value={a.interview?.structuredInterview || ''} onChange={e => setField('interview.structuredInterview', e.target.value || null)}>
              {['DIVA-5', 'ACE+', 'CAADID', 'other', 'none'].map(v => <MenuItem key={v} value={v}>{v === 'none' ? 'None' : v === 'other' ? 'Other' : v}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <TextField fullWidth multiline minRows={2} sx={{ mt: 2 }} label="Interview notes" disabled={!editable} value={a.interview?.notes || ''} onChange={e => setField('interview.notes', e.target.value)} />
      </Section>

      <Section title="Symptom areas" testId="dx-criteria">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Your determinations from the interview. Present in adulthood: inattention {counts.inattention?.adulthoodPresent ?? 0} of 9, hyperactivity and impulsivity {counts.hyperactivity_impulsivity?.adulthoodPresent ?? 0} of 9.
        </Typography>
        <Table size="small">
          <TableHead><TableRow><TableCell>Area</TableCell><TableCell>Adulthood</TableCell><TableCell>Childhood</TableCell><TableCell>Examples</TableCell></TableRow></TableHead>
          <TableBody>
            {a.criteria.map(c => (
              <TableRow key={c.key}>
                <TableCell sx={{ width: '32%' }}>{c.label}<Typography variant="caption" display="block" color="text.secondary">{c.domain === 'inattention' ? 'Inattention' : 'Hyperactivity and impulsivity'}</Typography></TableCell>
                {['adulthood', 'childhood'].map(f => (
                  <TableCell key={f} sx={{ width: 130 }}>
                    <Select size="small" fullWidth disabled={!editable} value={c[f] || ''} onChange={e => setCriterion(c.key, f, e.target.value || null)} inputProps={{ 'aria-label': `${c.label} ${f}` }}>
                      {DET.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                  </TableCell>
                ))}
                <TableCell><TextField size="small" fullWidth disabled={!editable} value={c.examples || ''} onChange={e => setCriterion(c.key, 'examples', e.target.value)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Section title="Onset, settings, impairment and informants">
        <FormControl sx={{ minWidth: 260, mb: 2 }} disabled={!editable}>
          <InputLabel id="onset">Present before about age 12?</InputLabel>
          <Select labelId="onset" label="Present before about age 12?" value={a.context?.onsetBefore12 || ''} onChange={e => setField('context.onsetBefore12', e.target.value || null)}>
            <MenuItem value="yes">Yes</MenuItem><MenuItem value="no">No</MenuItem><MenuItem value="unclear">Unclear</MenuItem>
          </Select>
        </FormControl>
        <Box>
          {SETTINGS.map(([k, l]) => (
            <FormControlLabel key={k} disabled={!editable} label={l} control={<Checkbox checked={(a.context?.settings || []).includes(k)}
              onChange={e => setField('context.settings', e.target.checked ? [...(a.context?.settings || []), k] : (a.context?.settings || []).filter(x => x !== k))} />} />
          ))}
        </Box>
        <TextField fullWidth multiline minRows={2} sx={{ mt: 1 }} label="Impairment" disabled={!editable} value={a.context?.impairmentNotes || ''} onChange={e => setField('context.impairmentNotes', e.target.value)} />
        <FormControlLabel sx={{ mt: 1 }} disabled={!editable} label="I have reviewed informant evidence" control={<Checkbox checked={!!a.context?.informantReviewed} onChange={e => setField('context.informantReviewed', e.target.checked)} />} />
        <TextField fullWidth multiline minRows={2} label="What the informant evidence shows" disabled={!editable} value={a.context?.informantNotes || ''} onChange={e => setField('context.informantNotes', e.target.value)} />
      </Section>

      <Section title="Differential diagnoses and co-occurring conditions">
        {a.differentials.map(d => (
          <Box key={d.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
            <FormControlLabel sx={{ minWidth: 340 }} disabled={!editable} label={d.label} control={<Checkbox checked={!!d.considered} onChange={e => setDiff(d.key, 'considered', e.target.checked)} />} />
            <TextField size="small" fullWidth placeholder="Notes" disabled={!editable} value={d.notes || ''} onChange={e => setDiff(d.key, 'notes', e.target.value)} />
          </Box>
        ))}
      </Section>

      <Section title="Conclusion">
        <FormControl fullWidth sx={{ mb: 2 }} disabled={!editable}>
          <InputLabel id="concl">Your conclusion</InputLabel>
          <Select labelId="concl" label="Your conclusion" value={a.outcome?.conclusion || ''} onChange={e => setField('outcome.conclusion', e.target.value || null)}>
            {Object.entries(CONCLUSIONS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField fullWidth multiline minRows={3} sx={{ mb: 2 }} label="Rationale" disabled={!editable} value={a.outcome?.rationale || ''} onChange={e => setField('outcome.rationale', e.target.value)} />
        <TextField fullWidth multiline minRows={2} label="Recommendations" disabled={!editable} value={a.outcome?.recommendations || ''} onChange={e => setField('outcome.recommendations', e.target.value)} />
      </Section>

      {editable && <Box sx={{ mb: 2 }}><Button variant="contained" onClick={save}>Save</Button></Box>}

      <Section title="Report" testId="dx-report">
        {editable && (
          <Button size="small" variant="outlined" sx={{ mb: 1 }} onClick={async () => { await save(); call(() => api.post(`/diagnostic/${id}/report/draft`), 'Report drafted from your entries. Review and edit it, then save.'); }}>
            Draft the report from my entries
          </Button>
        )}
        <TextField fullWidth multiline minRows={12} disabled={!editable} value={a.report?.text || ''} onChange={e => setField('report.text', e.target.value)} inputProps={{ 'aria-label': 'Report text' }} />
      </Section>

      {editable && (
        <Section title="Sign off" testId="dx-sign">
          {(a.signChecks?.blockers || []).length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>Before signing (save first): <ul style={{ margin: 0 }}>{a.signChecks.blockers.map(b => <li key={b}>{b}</li>)}</ul></Alert>
          )}
          {(a.signChecks?.warnings || []).map(w => (
            <FormControlLabel key={w.key} label={`I have considered this: ${w.text}`} control={<Checkbox checked={acks.includes(w.key)} onChange={e => setAcks(x => e.target.checked ? [...x, w.key] : x.filter(k => k !== w.key))} />} />
          ))}
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" color="success" disabled={(a.signChecks?.blockers || []).length > 0}
              onClick={() => call(() => api.post(`/diagnostic/${id}/sign`, { acknowledgedWarnings: acks }), 'Signed. The record is now locked; changes are made by amendment.')}>
              Sign the assessment
            </Button>
          </Box>
        </Section>
      )}

      {signed && isAssessor && (
        <Section title="Referral letter" testId="dx-referral">
          {!a.referral?.letterText ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="rt">To</InputLabel>
                <Select labelId="rt" label="To" value={referral.recipientType} onChange={e => setReferral(r => ({ ...r, recipientType: e.target.value }))}>
                  <MenuItem value="prescriber">Prescribing clinician</MenuItem><MenuItem value="gp">GP</MenuItem><MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Name" value={referral.recipientName} onChange={e => setReferral(r => ({ ...r, recipientName: e.target.value }))} />
              <TextField label="Address or email" value={referral.recipientContact} onChange={e => setReferral(r => ({ ...r, recipientContact: e.target.value }))} />
              <Button variant="outlined" onClick={() => call(() => api.post(`/diagnostic/${id}/referral/draft`, referral), 'Letter drafted.')}>Draft the letter</Button>
            </Box>
          ) : (
            <>
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, bgcolor: '#f6f8fb', p: 2 }}>{a.referral.letterText}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                <Button size="small" onClick={() => navigator.clipboard?.writeText(a.referral.letterText)}>Copy</Button>
                {a.referral.markedSentAt
                  ? <Chip size="small" color="success" variant="outlined" label={`Marked as sent ${fmtDate(a.referral.markedSentAt)}`} />
                  : <Button size="small" variant="outlined" onClick={() => call(() => api.post(`/diagnostic/${id}/referral/sent`), 'Marked as sent.')}>Mark as sent</Button>}
              </Box>
            </>
          )}
        </Section>
      )}

      {signed && (
        <Section title="Amendments">
          {(a.amendments || []).length === 0 ? <Typography variant="body2">None.</Typography> : a.amendments.map((m, i) => (
            <Typography key={i} variant="body2">{fmtDate(m.at)}: {m.reason} ({Object.keys(m.changes?.after || {}).join(', ')})</Typography>
          ))}
          {isAssessor && (
            <Button size="small" sx={{ mt: 1 }} onClick={() => { setAmend({ reason: '', rationale: a.outcome?.rationale || '', recommendations: a.outcome?.recommendations || '', report: a.report?.text || '' }); setAmendOpen(true); }}>
              Amend
            </Button>
          )}
        </Section>
      )}

      <Box sx={{ mb: 4 }}>
        <Button size="small" onClick={async () => setAudit((await api.get(`/diagnostic/${id}/audit`)).data.audit)}>Show the process record</Button>
        {audit && (
          <Typography component="pre" data-testid="dx-audit" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, bgcolor: '#f6f8fb', p: 2, mt: 1 }}>{JSON.stringify(audit, null, 2)}</Typography>
        )}
      </Box>

      <Dialog open={amendOpen} onClose={() => setAmendOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Amend the signed assessment</DialogTitle>
        <DialogContent>
          <TextField fullWidth required sx={{ mt: 1, mb: 2 }} label="Reason for the amendment" value={amend.reason} onChange={e => setAmend(x => ({ ...x, reason: e.target.value }))} />
          <TextField fullWidth multiline minRows={2} sx={{ mb: 2 }} label="Rationale" value={amend.rationale} onChange={e => setAmend(x => ({ ...x, rationale: e.target.value }))} />
          <TextField fullWidth multiline minRows={2} sx={{ mb: 2 }} label="Recommendations" value={amend.recommendations} onChange={e => setAmend(x => ({ ...x, recommendations: e.target.value }))} />
          <TextField fullWidth multiline minRows={8} label="Report" value={amend.report} onChange={e => setAmend(x => ({ ...x, report: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAmendOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!amend.reason.trim()} onClick={async () => {
            const changes = {};
            if (amend.rationale !== (a.outcome?.rationale || '') || amend.recommendations !== (a.outcome?.recommendations || '')) changes.outcome = { rationale: amend.rationale, recommendations: amend.recommendations };
            if (amend.report !== (a.report?.text || '')) changes.report = { text: amend.report };
            await call(() => api.post(`/diagnostic/${id}/amend`, { reason: amend.reason, changes }), 'Amendment recorded.');
            setAmendOpen(false);
          }}>Record amendment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function DiagnosticAssessments() {
  const { id } = useParams();
  return id ? <Workspace id={id} /> : <MyList />;
}
